import {
  useCallback, useState, memo, useMemo,
} from 'react';
import {
  ActionButton,
  Button,
  Divider,
  encryptSymmetric,
  Grid,
  MoveIcon,
  PanelContent,
  PlusIcon,
  SearchableTextField,
  Tooltip,
  FilterButton,
  usePopper,
  useSnackbar,
  useTranslations,
  usePubSub,
  ShareIcon,
  ErrorIcon,
  useMapKeyValueExtractor,
  PromptModal,
  Typography,
  ISearchableTextFieldProps,
  IFilterButtonProps,
} from '@uniqkey-frontend/shared-app';
import { EmployeeAccountStatus, Ownership } from '@uniqkey-backend-organization-web/api-client';
import PubSubEventEnum from '../../../../enums/PubSubEventEnum';
import useEmployeeGroupLoginsTable, {
  IEmployeeGroupLoginsTableRow,
} from '../../../../hooks/tables/useEmployeeGroupLoginsTable';
import EmployeeLoginsTable from '../../../../components/tables/EmployeeLoginsTable';
import CreateLoginModal, { ICreateLoginReturnValue } from '../../../../components/CreateLoginModal';
import { useCompanionApplicationContext } from '../../../../contexts/CompanionApplicationContext';
import useVaultsToEmployeeAccountsAPI from '../../../../hooks/useVaultsToEmployeeAccountsAPI';
import {
  useDeleteVaultsToEmployeeAccounts,
  useGetEmployeeAccountById,
} from '../../../../hooks/reactQuery';
import EmployeeLoginsTabFilter, {
  IEmployeeLoginsTabFilterSubmitResult,
} from './components/EmployeeLoginsTabFilter';
import ShareVaultsToGroupModal from '../../../../components/ShareVaultsToGroupModal';
import useVaultsAPI from '../../../../hooks/useVaultsAPI';
import VaultTypeEnum from '../../../../enums/VaultTypeEnum';
import { logException } from '../../../../services/sentryService';
import { getActiveOrganizationId } from '../../../../services/organizationService';
import HostTypeEnum from '../../../../enums/HostTypeEnum';
import { generateTooltipTitle } from '../../../../helpers/tooltips';
import { useTrustedPortalStore } from '../../../../modules/TrustedPortalModule/store';

interface IEmployeeLoginsTabProps {
  employeeAccountId: string;
  employeeAccountStatus: EmployeeAccountStatus;
}

const EmployeeLoginsTab = (props: IEmployeeLoginsTabProps) => {
  const { employeeAccountId, employeeAccountStatus } = props;
  const { t } = useTranslations();
  const [isCreateLoginLoading, setIsCreateLoginLoading] = useState(false);
  const [isDeleteLoginLoading, setIsDeleteLoginLoading] = useState(false);
  const [isMoveLoginsLoading, setIsMoveLoginsLoading] = useState<boolean>(false);
  const [isMoveLoginsModalOpen, setIsMoveLoginsModalOpen] = useState<boolean>(false);
  const [isShareLoginsModalOpen, setIsShareLoginsModalOpen] = useState<boolean>(false);
  const [isCreateLoginModalOpen, setIsCreateLoginModalOpen] = useState(false);
  const [isDeleteLoginModalOpen, setIsDeleteLoginModalOpen] = useState(false);

  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );
  const handleFilterClose = useCallback(() => setIsFilterOpen(false), [setIsFilterOpen]);
  const handleMoveLoginsModalOpen = useCallback(() => setIsMoveLoginsModalOpen(true), []);
  const handleMoveLoginsModalClose = useCallback(() => setIsMoveLoginsModalOpen(false), []);
  const handleCreateLoginModalOpen = useCallback(() => setIsCreateLoginModalOpen(true), []);
  const handleCreateLoginModalClose = useCallback(() => setIsCreateLoginModalOpen(false), []);
  const handleDeleteLoginModalOpen = useCallback(() => setIsDeleteLoginModalOpen(true), []);
  const handleDeleteLoginModalClose = useCallback(() => setIsDeleteLoginModalOpen(false), []);
  const handleShareLoginsModalOpen = useCallback(() => setIsShareLoginsModalOpen(true), []);
  const handleShareLoginsModalClose = useCallback(() => setIsShareLoginsModalOpen(false), []);

  const {
    selectedLogins,
    refetch,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps
  } = useEmployeeGroupLoginsTable({
    persistentFilters: { employeeAccountId },
    noDataMessageKey: 'loginsTab.table.noData',
  });
  const { data: employeeAccount } = useGetEmployeeAccountById(
    { employeeAccountId },
  );
  const {
    values: selectedLoginsAsObjects, keys: selectedLoginsIds,
  } = useMapKeyValueExtractor<IEmployeeGroupLoginsTableRow>(selectedLogins);

  const handleRefetch = useCallback(() => refetch(true), [refetch]);
  usePubSub(PubSubEventEnum.DATA_SYNCHRONIZATION_VAULT, handleRefetch);

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((updatedValues: IEmployeeLoginsTabFilterSubmitResult) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);
  const { showError, showWarning, showSuccess } = useSnackbar();
  const { symmetricKey } = useCompanionApplicationContext();
  const { createVaultsToEmployeeAccounts } = useVaultsToEmployeeAccountsAPI();
  const { moveVaultsToPrivateKeychain } = useVaultsAPI();
  const { mutate: mutateDeleteLogin } = useDeleteVaultsToEmployeeAccounts();

  const activeOrganizationId = getActiveOrganizationId();
  const isTrustedPortalEnabled = useTrustedPortalStore.useIsEnabledByOrganizationId()[
    activeOrganizationId!
  ] ?? false;

  const handleCreateLogin = useCallback(async (params: ICreateLoginReturnValue) => {
    const {
      name, additionalInfo, username, password, hostType, website, desktopApp,
    } = params;
    setIsCreateLoginLoading(true);
    try {
      let payload = {
        name,
        username,
        additionalInfo,
        employeeAccountId,
        vaultType: VaultTypeEnum.Password,
        password: encryptSymmetric({
          key: symmetricKey,
          string: password,
          fallbackValue: null,
        }),
      };
      if (hostType === HostTypeEnum.Website) {
        payload = { ...payload, ...(website && { webAddress: website }) };
      }
      if (hostType === HostTypeEnum.DesktopApp) {
        payload = {
          ...payload,
          ...(desktopApp?.value && {
            desktopApplicationName: desktopApp.value,
            applicationId: desktopApp.id,
          }),
        };
      }
      await createVaultsToEmployeeAccounts(payload);
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.login.created'),
        });
      } else {
        showWarning({
          text: t('createLoginModal.approvalOnMobileToast'),
        });
      }
      handleCreateLoginModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'EmployeeLoginsTab/handleCreateLogin exception',
      });
    } finally {
      setIsCreateLoginLoading(false);
    }
  }, [
    employeeAccountId,
    symmetricKey,
    handleCreateLoginModalClose,
    createVaultsToEmployeeAccounts,
    isTrustedPortalEnabled,
    showSuccess,
    showWarning,
    showError,
    t,
  ]);

  const handleDeleteLogin = useCallback(async () => {
    setIsDeleteLoginLoading(true);
    mutateDeleteLogin({
      vaultIds: selectedLoginsIds, employeeAccountId,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t('deleteLoginModal.successMessage', { count: successCount }),
          });
        }
        if (failCount) {
          showError({
            text: t('deleteLoginModal.errorMessage', { count: failCount }),
          });
        }
        handleDeleteLoginModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: () => showError({ text: t('common.somethingWentWrong') }),
      onSettled: () => setIsDeleteLoginLoading(false),
    });
  }, [
    employeeAccountId,
    selectedLoginsIds,
    mutateDeleteLogin,
    handleDeleteLoginModalClose,
    showSuccess,
    showError,
    resetSelectedRows,
    resetActivePage,
    t,
  ]);

  const handleMoveLogins = useCallback(async () => {
    try {
      setIsMoveLoginsLoading(true);
      await moveVaultsToPrivateKeychain({ vaultIds: selectedLoginsIds, employeeAccountId });
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.login.moved'),
        });
      } else {
        showWarning({
          text: t('moveLoginsModal.approvalOnMobileToast'),
        });
      }
      resetSelectedRows();
      handleMoveLoginsModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'EmployeeLoginsTab/handleMoveLogins exception',
      });
    } finally {
      setIsMoveLoginsLoading(false);
    }
  }, [
    moveVaultsToPrivateKeychain,
    selectedLoginsIds,
    isTrustedPortalEnabled,
    showWarning,
    showError,
    showSuccess,
    handleMoveLoginsModalClose,
    employeeAccountId,
    resetSelectedRows,
    t,
  ]);

  const {
    isMoveDisabled, isShareDisabled, isRemoveDisabled,
  } = useMemo(() => {
    if (!selectedLoginsAsObjects.length) {
      return { isMoveDisabled: true, isShareDisabled: true, isRemoveDisabled: true };
    }
    let disableMove = false;
    let disableShare = false;
    let disableRemove = false;
    selectedLoginsAsObjects.forEach(({ ownership }) => {
      if (ownership === Ownership.Groups) {
        disableMove = true;
        disableRemove = true;
      }
      if (ownership === Ownership.Employees) {
        disableShare = true;
      }
    });
    return {
      isMoveDisabled: disableMove,
      isShareDisabled: disableShare,
      isRemoveDisabled: disableRemove,
    };
  }, [selectedLoginsAsObjects]);

  const moveLoginsModalDescriptionElement = useMemo(
    () => (
      <>
        <Typography>
          {t('moveLoginsModal.description', { email: employeeAccount?.email })}
        </Typography>
        <Typography>{t('moveLoginsModal.additionalDescription')}</Typography>
      </>
    ),
    [employeeAccount?.email, t],
  );

  const { moveTooltipTitle, shareTooltipTitle, removeTooltipTitle } = useMemo(() => {
    const moveTitle = generateTooltipTitle({
      selectedDataLength: selectedLoginsAsObjects.length,
      t,
      isDisabled: isMoveDisabled,
      key: 'loginsTab.move',
    });
    const shareTitle = generateTooltipTitle({
      selectedDataLength: selectedLoginsAsObjects.length,
      t,
      isDisabled: isShareDisabled,
      key: 'loginsTab.share',
    });
    const removeTitle = generateTooltipTitle({
      selectedDataLength: selectedLoginsAsObjects.length,
      t,
      isDisabled: isRemoveDisabled,
      key: 'loginsTab.remove',
    });

    return {
      moveTooltipTitle: moveTitle,
      shareTooltipTitle: shareTitle,
      removeTooltipTitle: removeTitle,
    };
  }, [selectedLoginsAsObjects.length, t, isMoveDisabled, isShareDisabled, isRemoveDisabled]);

  const isCreateLoginShown = useMemo(() => (
    employeeAccountStatus !== EmployeeAccountStatus.Archived
  ), [employeeAccountStatus]);

  const isMoveLoginShown = useMemo(() => (
    employeeAccountStatus !== EmployeeAccountStatus.Archived
    && employeeAccountStatus !== EmployeeAccountStatus.Staged
    && employeeAccountStatus !== EmployeeAccountStatus.Invited
  ), [employeeAccountStatus]);

  return (
    <PanelContent p={0}>
      <Grid container justifyContent="space-between" alignItems="stretch" p={1}>
        <Grid item xs={4} container flexWrap="nowrap" spacing={1}>
          <Grid item>
            <Tooltip title={t('common.filter')}>
              <FilterButton
                isFilterActive={isFilterActive}
                numberOfActiveFilters={numberOfActiveFilters}
                selected={isFilterOpen}
                onChange={toggleIsFilterOpen}
              />
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
          {isMoveLoginShown && (
            <Grid item alignSelf="center">
              <Tooltip title={moveTooltipTitle}>
                <ActionButton
                  width={40}
                  height={40}
                  onClick={handleMoveLoginsModalOpen}
                  disabled={isMoveDisabled}
                >
                  <MoveIcon />
                </ActionButton>
              </Tooltip>
            </Grid>
          )}
          <Grid item alignSelf="center">
            <Tooltip title={shareTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleShareLoginsModalOpen}
                disabled={isShareDisabled}
              >
                <ShareIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={removeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDeleteLoginModalOpen}
                disabled={isRemoveDisabled}
              >
                <ErrorIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
        </Grid>
        <Grid item xs={8} container justifyContent="flex-end" flexWrap="nowrap">
          <Grid item>
            <SearchableTextField
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('common.search')}
            />
          </Grid>
          {isCreateLoginShown && (
            <Grid item ml={3}>
              <Button
                icon={<PlusIcon />}
                onClick={handleCreateLoginModalOpen}
              >
                {t('loginsTab.createLoginButton')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Divider />
      <EmployeeLoginsTable
        selectedLogins={selectedLogins}
        {...restTableProps}
      />
      <EmployeeLoginsTabFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues as IEmployeeLoginsTabFilterSubmitResult}
      />
      {isCreateLoginModalOpen && (
        <CreateLoginModal
          isOpen={isCreateLoginModalOpen}
          onClose={handleCreateLoginModalClose}
          onSubmit={handleCreateLogin}
          isLoading={isCreateLoginLoading}
        />
      )}
      {isDeleteLoginModalOpen && (
        <PromptModal
          open={isDeleteLoginModalOpen}
          onClose={handleDeleteLoginModalClose}
          onSubmit={handleDeleteLogin}
          title={t(
            'deleteLoginModal.deleteFromEmployee.title',
            { count: selectedLogins.size },
          )}
          description={t(
            'deleteLoginModal.deleteFromEmployee.description',
            { email: employeeAccount?.email },
          )}
          additionalDescription={t(
            'deleteLoginModal.deleteFromEmployee.additionalDescription',
          )}
          list={selectedLoginsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDeleteLoginLoading}
        />
      )}
      {isMoveLoginsModalOpen && (
        <PromptModal
          open={isMoveLoginsModalOpen}
          onClose={handleMoveLoginsModalClose}
          onSubmit={handleMoveLogins}
          title={t('moveLoginsModal.title', { count: selectedLogins.size })}
          description={moveLoginsModalDescriptionElement}
          list={selectedLoginsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isMoveLoginsLoading}
          approvalButtonText="common.move"
          approvalButtonColor="primary"
        />
      )}
      {isShareLoginsModalOpen && (
        <ShareVaultsToGroupModal
          isOpen={isShareLoginsModalOpen}
          onClose={handleShareLoginsModalClose}
          vaults={selectedLoginsAsObjects}
          type={VaultTypeEnum.Password}
          onSubmit={resetSelectedRows}
        />
      )}
    </PanelContent>
  );
};

export default memo(EmployeeLoginsTab);
